import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../App";
import SupportPanel from "../SupportPanel";
import Mascot from "../Mascot";
import { Instagram } from "lucide-react";
import {
  User, Gamepad2, BarChart3, Wallet, Gift, Trophy, Users,
  HelpCircle, FileText, ChevronDown, ChevronRight, Settings,
  Crown, Star, Shield, Sparkles, Target, Megaphone, Rocket, Dice1, CircleDot, Spade,
  Fish, LayoutGrid, Zap
} from "lucide-react";
import toast from "react-hot-toast";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dc2ea8e9-88c1-4191-a09e-3c70ac39f874/artifacts/lxlb74jo_IMG_9876.jpeg";
const ACCENT = "#4d7cff";

const VIP_TIERS = [
  { level: 0, name: "Bronze", color: "text-amber-700", gradFrom: "from-amber-800", gradTo: "to-amber-600", minPurchase: 0 },
  { level: 1, name: "Silver", color: "text-gray-300", gradFrom: "from-gray-500", gradTo: "to-gray-300", minPurchase: 50 },
  { level: 2, name: "Gold", color: "text-yellow-400", gradFrom: "from-yellow-600", gradTo: "to-yellow-400", minPurchase: 200 },
  { level: 3, name: "Platinum", color: "text-cyan-400", gradFrom: "from-cyan-600", gradTo: "to-cyan-300", minPurchase: 500 },
  { level: 4, name: "Diamond", color: "text-blue-400", gradFrom: "from-blue-600", gradTo: "to-blue-400", minPurchase: 1000 },
  { level: 5, name: "Royal", color: "text-purple-400", gradFrom: "from-purple-600", gradTo: "to-purple-400", minPurchase: 5000 },
];

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState(["promo", "games", "arcade-games"]);
  const [showSupport, setShowSupport] = useState(false);
  const [disabledGameIds, setDisabledGameIds] = useState([]);
  const [disabledProviders, setDisabledProviders] = useState([]);
  const isAdmin = user?.email === "admin@jackroyals.com" || user?.is_admin || user?.role === "admin";

  // Fetch disabled games + providers
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/disabled-games-list`).then(r => r.json()).then(d => {
      setDisabledGameIds(d.disabled_game_ids || []);
      setDisabledProviders(d.disabled_providers || []);
    }).catch(() => {});
  }, []);

  // Normalize both sides so "ka_gaming" matches "KA Gaming"
  const _normProv = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const isProviderDisabled = (slug) => {
    const n = _normProv(slug);
    if (!n) return false;
    return disabledProviders.some(dp => {
      const d = _normProv(dp);
      return d && (n.includes(d) || d.includes(n));
    });
  };

  const prevTierRef = useRef(null);
  useEffect(() => {
    const vipRankFromServer = user?.vip_rank;
    if (!vipRankFromServer) return;
    const storedTier = sessionStorage.getItem("jr_last_vip_tier");
    if (storedTier && storedTier !== vipRankFromServer) {
      const newTierInfo = VIP_TIERS.find(t => t.name === vipRankFromServer);
      const oldTierInfo = VIP_TIERS.find(t => t.name === storedTier);
      if (newTierInfo && oldTierInfo && newTierInfo.level > oldTierInfo.level) {
        toast.success(`Congratulations! You've reached ${vipRankFromServer} Tier! Your daily bonus has been upgraded.`, { duration: 8000 });
      }
    }
    sessionStorage.setItem("jr_last_vip_tier", vipRankFromServer);
    prevTierRef.current = vipRankFromServer;
  }, [user?.vip_rank]);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]);
  };

  const menuItems = [
    { id: "arcade-hub", label: "Arcade Hub", icon: Sparkles, path: "/arcade", expandable: false, highlight: true, badge: "NEW" },
    { id: "profile", label: "Profile Settings", icon: User, path: "/profile", expandable: false },
    { id: "verification", label: "Verify Account", icon: Shield, path: "/verification", expandable: false, highlight: user?.verification_status !== "verified" && user?.verification_status !== "pending", badge: user?.verification_status === "pending" ? "..." : user?.verification_status !== "verified" ? "!" : null, badgeColor: user?.verification_status === "pending" ? "bg-amber-500" : "bg-red-500", hideWhenVerified: user?.verification_status === "verified" },
    { id: "gameplay", label: "My Arcade", icon: Gamepad2, path: "/lobby", expandable: false },
    { id: "stats", label: "My Stats", icon: BarChart3, path: "/leaderboard", expandable: false },
    { id: "tournaments", label: "Tournaments", icon: Trophy, path: "/tournaments", expandable: false, badge: "NEW" },
    { id: "wallet", label: "Wallet", icon: Wallet, path: "/store", expandable: false },
    { id: "support", label: "Support", icon: HelpCircle, path: "#support", expandable: false, onClick: () => setShowSupport(true) },
    {
      id: "games", label: "GAMES", icon: LayoutGrid, expandable: true,
      children: [
        { id: "fish-games", label: "Fish Games", icon: Fish, path: "/lobby?category=fish", badge: "HOT" },
        { id: "slot-games", label: "Arcade Games", icon: Sparkles, path: "/lobby?category=slots" },
        { id: "new-releases", label: "New Releases", icon: Zap, path: "/lobby?category=new", badge: "NEW" },
        { id: "classics", label: "Classics", icon: Crown, path: "/lobby?category=classic" },
        { id: "featured", label: "Featured", icon: Target, path: "/lobby?category=featured" },
        { id: "all-games", label: "All Games", icon: LayoutGrid, path: "/lobby" },
      ],
    },
    {
      id: "arcade-games", label: "Arcade Games", icon: Gamepad2, expandable: true,
      children: [
        { id: "crash", label: "Crash", icon: Rocket, path: "/game/crash", badge: "HOT", provider: "jackroyals" },
        { id: "roulette", label: "Spin Wheel", icon: CircleDot, path: "/game/roulette", provider: "jackroyals" },
        { id: "blackjack", label: "21 Challenge", icon: Spade, path: "/game/blackjack", provider: "jackroyals" },
        { id: "poker", label: "Card Arena", icon: Dice1, path: "/game/poker", provider: "jackroyals" },
        { id: "daily-wheel", label: "Daily Wheel", icon: Gift, path: "/daily-wheel", badge: "FREE" },
        { id: "jackpots", label: "Prize Pools", icon: Trophy, path: "/jackpots" },
      ],
    },
    {
      id: "promo", label: "Rewards", icon: Gift, expandable: true,
      children: [
        { id: "rewards", label: "My Rewards", icon: Gift, path: "/rewards" },
        { id: "giveaways", label: "Giveaways", icon: Trophy, path: "/giveaways", badge: "NEW" },
        { id: "challenges", label: "Challenges", icon: Target, path: "/challenges" },
        { id: "refer", label: "Refer a Friend", icon: Users, path: "/referral" },
        { id: "promotions", label: "Promotions", icon: Star, path: "/store" },
      ],
    },
    { id: "terms", label: "Terms and Conditions", icon: FileText, path: "/terms", expandable: false },
    { id: "responsible-play", label: "Responsible Play", icon: Shield, path: "/responsible-gaming", expandable: false },
  ];

  if (isAdmin) {
    menuItems.splice(5, 0, { id: "admin", label: "Admin Dashboard", icon: Settings, path: "/admin", expandable: false, highlight: true });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={onClose} />}

      <aside className={`fixed lg:sticky top-0 left-0 h-screen z-50 w-72 flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} style={{ background: 'linear-gradient(180deg, #0a0f1f, #050814)', borderRight: '1px solid rgba(120,150,255,0.15)' }}>
        {/* Refer a Friend */}
        <div className="px-4 pt-4 mb-4">
          <Link to="/referral" className="block">
            <div className="relative overflow-hidden rounded-xl p-4 group transition-colors" style={{ background: `linear-gradient(135deg, ${ACCENT}20, ${ACCENT}10)`, border: `1px solid ${ACCENT}40` }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${ACCENT}20` }}>
                  <Users className="w-6 h-6" style={{ color: ACCENT }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Refer a Friend</p>
                  <p className="text-xs text-[#8fa2d6]">and earn</p>
                  <p className="text-sm font-bold text-emerald-400">FREE SC</p>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 space-y-1">
          {menuItems.filter(item => !item.hideWhenVerified).map((item) => {
            const Icon = item.icon;
            const isExpanded = expandedMenus.includes(item.id);

            if (item.expandable) {
              return (
                <div key={item.id}>
                  <button onClick={() => toggleMenu(item.id)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${isExpanded ? 'text-white' : 'text-[#8fa2d6] hover:text-white'}`} style={isExpanded ? { background: `${ACCENT}15`, color: ACCENT } : { background: 'transparent' }}>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {isExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.filter(child => !disabledGameIds.includes(child.id) && !(child.provider && isProviderDisabled(child.provider))).map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          <Link key={child.id} to={child.path} onClick={onClose} className={`flex items-center justify-between px-4 py-2.5 rounded-xl transition-all ${isActive(child.path) ? 'text-white font-bold' : 'text-[#8fa2d6] hover:text-white'}`} style={isActive(child.path) ? { background: ACCENT } : { background: 'transparent' }}>
                            <div className="flex items-center gap-3">
                              <ChildIcon className="w-4 h-4" />
                              <span className="text-sm">{child.label}</span>
                            </div>
                            {child.badge && (
                              <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${child.badge === "HOT" ? "bg-red-500 text-white" : child.badge === "NEW" ? "text-white" : child.badge === "FREE" ? "bg-emerald-500 text-white" : "text-black"}`} style={child.badge === "NEW" ? { background: ACCENT } : child.badge !== "HOT" && child.badge !== "FREE" ? { background: ACCENT } : {}}>{child.badge}</span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              item.onClick ? (
                <button key={item.id} onClick={item.onClick} className="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-[#8fa2d6] hover:text-white" style={{ background: 'transparent' }}>
                  <div className="flex items-center gap-3"><Icon className="w-5 h-5" /><span className="font-medium">{item.label}</span></div>
                </button>
              ) : (
                <Link key={item.id} to={item.path} onClick={onClose} className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${isActive(item.path) ? 'text-white font-bold' : item.highlight ? 'hover:bg-white/5' : 'text-[#8fa2d6] hover:text-white'}`} style={isActive(item.path) ? { background: ACCENT } : item.highlight ? { color: ACCENT } : { background: 'transparent' }}>
                  <div className="flex items-center gap-3"><Icon className="w-5 h-5" /><span className="font-medium">{item.label}</span></div>
                  {item.badge && <span className={`w-5 h-5 rounded-full ${item.badgeColor || 'bg-red-500'} text-white text-xs flex items-center justify-center font-bold`}>{item.badge}</span>}
                </Link>
              )
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 space-y-3" style={{ borderTop: '1px solid rgba(120,150,255,0.1)' }}>
          {/* Instagram growth hook — Valkyrie warrior calls the player to daily battles/giveaways */}
          <a
            href="https://www.instagram.com/jackroyals_/"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center gap-3 px-3 py-2.5 rounded-xl overflow-hidden transition-all hover:scale-[1.02]"
            style={{ background: "linear-gradient(135deg, #833AB4, #E1306C 45%, #F77737 85%)" }}
            data-testid="sidebar-instagram-cta"
          >
            <div className="flex-shrink-0">
              <Mascot slug="viking_warrior" size="sm" role="inline" tip="Daily Battles & Giveaways ⚔️" className="!w-12 !h-12" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <Instagram className="w-3.5 h-3.5 text-white" />
                <span className="text-[11px] font-black text-white tracking-wider">@JackRoyals</span>
              </div>
              <p className="text-[10px] leading-tight text-white/90 font-semibold mt-0.5">
                Follow for <span className="font-black">Daily SC Drops</span>
              </p>
            </div>
            <span className="text-[10px] font-black text-white/90 uppercase tracking-widest">Go →</span>
          </a>

          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="JackRoyals" className="w-8 h-8 rounded-lg" />
            <div>
              <p className="text-xs text-[#5a6b8a]">&copy; 2026 Jack Royals LLC</p>
              <p className="text-xs text-[#3d4f70]">Play Responsibly. 18+</p>
            </div>
          </div>
        </div>
      </aside>

      <SupportPanel isOpen={showSupport} onClose={() => setShowSupport(false)} />
    </>
  );
};

export default Sidebar;
